import { RollbackOutlined, SwapOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Descriptions,
  Empty,
  Form,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Typography,
} from 'antd';
import axios from 'axios';
import { useContext, useEffect, useState } from 'react';
import { AiOutlineInfoCircle } from 'react-icons/ai';

import SectionCard from '../../../../components/sectionCard/SectionCard';
import StepFooter from '../../../../components/stepFooter/StepFooter';
import { getBaseSku } from '../../../../utils/format';
import { AddOrderDeliveryContext } from '../AddOrderDeliveryContext';
import { AddOrderReturnContext } from '../addOrderReturnContext';
import Branch from '../Branch';
import Warehouse from '../Warehouse';
import ModalSelectOrder from './ModalSelectOrder';
import Product from './Product';

const { confirm } = Modal;
const { Text } = Typography;

/** Талбарууд картын бүтэн өргөнөөр сунахгүй — зүүн талд багцлагдана */
const FORM_MAX_WIDTH = 960;

const PRODUCT_NOT_CHANGE = 'productNotChange';

/** Хоосон утгыг "-" болгож харуулна */
const val = (value) => (value === null || value === undefined || value === '' ? '-' : value);

/** Descriptions-ийн ерөнхий тохиргоо: label дээр, value доор, зай багатай */
const descriptionProps = {
  size: 'small',
  colon: false,
  layout: 'vertical',
  column: { xs: 1, sm: 2, lg: 4 },
  labelStyle: { paddingBottom: 0 },
  contentStyle: { paddingBottom: 0 },
};

export const Step1 = ({ nextStep }) => {
  const [form] = Form.useForm();

  const {
    fromBranch,
    setFromBranch,
    fromChoice,
    setFromChoice,
    warehouseList,
    setWareHouseList,
    clearSelectedWareHouses,
    addWarehouse,
    addBranch,
    branchList,
    setBranchList,
    fromWarehouse,
    setFromWareHouse,
    clearSelectedBranches,
    setDeliveryReference,
  } = useContext(AddOrderDeliveryContext) ?? {};

  const { selectedOrder, selectOrder, setReference, formDataStep1, setFormDataStep1 } =
    useContext(AddOrderReturnContext);

  const [addressArray, setAddressArray] = useState([]);
  const [visibleOrder, setVisibleOrder] = useState(false);
  const [customFields, setCustomFields] = useState([]);
  const [contractType, setContractType] = useState();
  const [referenceLoading, setReferenceLoading] = useState(false);

  const fetchReference = async () => {
    setReferenceLoading(true);
    try {
      const { data } = await axios.get('/api/v1/request/return-reference');

      if (data?.statusCode === 200) {
        setReference(data?.result);
        setContractType(data?.result?.merchantContract?.data?.contractType);
      }
    } catch (error) {
      console.error('fetchReference: ', error);
    } finally {
      setReferenceLoading(false);
    }
  };

  useEffect(() => {
    fetchReference();

    if (fromChoice) {
      form.setFieldValue('exchangeChoice', fromChoice);
      return;
    }

    /**
     * Унтраалгыг ЗӨВХӨН солих бараа огт сонгоогүй үед л унтраана.
     *
     * Step1 нь алхам солиход unmount болдог тул 2-р алхмаас буцаж ирэхэд энэ
     * effect дахин ажилладаг. Өмнө нь болзолгүйгээр унтраадаг байсан учир
     * сонгосон бараа контекстэд байсаар атлаа "Солих бараа" блок хаагдаж,
     * мэдээлэл алга болсон мэт харагддаг байв.
     */
    const hasExchangeProducts = [...(warehouseList ?? []), ...(branchList ?? [])].some(
      (item) => item.products?.length
    );
    if (hasExchangeProducts) return;

    setFromBranch(false);
    setFromWareHouse(false);
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    axios
      .get('/api/v1/request/delivery-reference')
      .then(({ data }) => {
        if (data.statusCode === 200) setDeliveryReference(data?.result);
      })
      .catch((err) => {
        console.error('delivery-reference: ', err);
      });
    //eslint-disable-next-line
  }, []);

  const onChangeChoices = (e) => {
    const checked = e;
    setFromChoice(checked);

    if (checked === 'branch') {
      setFromBranch(true);
      onChangeSelectBranch(checked);
      if (fromWarehouse) onChangeSelectWareHouse(false);
    }
    if (checked === 'warehouse') {
      setFromWareHouse(true);
      onChangeSelectWareHouse(checked);
      if (fromBranch) onChangeSelectBranch(false);
    }
    if (!checked) {
      fromBranch && onChangeSelectBranch(checked);
      fromWarehouse && onChangeSelectWareHouse(checked);
      form.setFieldValue('exchangeChoice', false);
    }
  };

  const onChangeSelectBranch = (checked) => {
    if (!checked) {
      confirm({
        title: 'Салбараас татахаар сонгосон бараануудыг устгах уу?',
        okText: 'Тийм',
        cancelText: 'Үгүй',
        onOk: () => {
          setFromBranch(false);
          setBranchList([]);
          clearSelectedBranches();
        },
      });
      return;
    }
    if (branchList?.length === 0) addBranch();
    setFromBranch(checked);
  };

  const onChangeSelectWareHouse = (checked) => {
    if (!checked) {
      confirm({
        title: 'Агуулахаас татахаар сонгосон бараануудыг устгах уу?',
        okText: 'Тийм',
        cancelText: 'Үгүй',
        onOk: () => {
          setFromWareHouse(false);
          setWareHouseList([]);
          clearSelectedWareHouses();
        },
      });
      return;
    }
    if (warehouseList?.length === 0) addWarehouse();
    setFromWareHouse(checked);
  };

  useEffect(() => {
    if (!selectedOrder) return;

    const address = selectedOrder?.dropoffAddressArray ?? [];
    if (address?.length > 2) {
      form.setFieldsValue({
        aimagHot: address[0].addressId ?? '',
        sumDuureg: address[1].addressId ?? '',
        bagHoroo: address[2].addressId ?? '',
      });
      setAddressArray(address);
    }
  }, [selectedOrder, form]);

  useEffect(() => {
    form.setFieldsValue({ deliveryType: '' });
    setCustomFields([]);
  }, [addressArray, form]);

  const getCabinetAddress = (deliveryPositionCode) => {
    const field = customFields.find((item) => item.enum?.includes(deliveryPositionCode));
    if (!field) return '';
    return field.enumDictionary[`${deliveryPositionCode}`]?.addressName;
  };

  const onFinish = (values) => {
    // Солих бараа
    const exchangeProducts = [];
    [...warehouseList, ...branchList].forEach((item) =>
      item.products.forEach((p) => {
        const obj = {
          baseSku: getBaseSku(p.productSku),
          productSKU: p.productSku,
          productTitle: p.title,
          productImage: p.imageCover,
          productPrice: p.price,
          productTotalUnit: p.totalUnit,
          productTotalWeight: p.weight * p.totalUnit,
          productLocation: p.productLocation,
          productLocationCode: p.productLocationCode,
          productGroup: p.isGroup,
          productGroups: p.group || null,
        };
        if (p.productPositionCode) obj.productPositionCode = p.productPositionCode;
        if (p.batchCode) obj.batchCode = p.batchCode;
        if (p.expiryDate) obj.expiryDate = p.expiryDate;
        exchangeProducts.push(obj);
      })
    );

    if (values.deliveryPositionCode) {
      values.cabinetAddress = getCabinetAddress(values.deliveryPositionCode);
    }

    setFormDataStep1({
      values,
      formValues: form.getFieldsValue(),
      products: exchangeProducts,
    });
    nextStep();
  };

  /** Гэрээний төрлөөс хамаарсан "Солих бараа" картын унтраалга */
  const exchangeSwitch = () => {
    // Гэрээний төрөл (reference) ачаалагдаж дуустал унтраалга харагдахгүй
    if (referenceLoading) return <Spin size="small" />;
    if (contractType === 'O') {
      return <Switch name="fromBranch" checked={fromBranch} onChange={onChangeSelectBranch} />;
    }
    if (contractType === 'W') {
      return (
        <Switch name="fromWarehouse" checked={fromWarehouse} onChange={onChangeSelectWareHouse} />
      );
    }
    if (contractType === 'WO') {
      return <Switch name="chooseFrom" checked={Boolean(fromChoice)} onChange={onChangeChoices} />;
    }
    return null;
  };

  // Захиалга сонгоогүй үед: зөвхөн сонгох карт харагдана
  if (!selectedOrder) {
    return (
      <>
        {/* Захиалга сонгосон үед л талбарууд гарах ч формын instance-ыг холбоотой үлдээв */}
        <Form form={form} layout="vertical">
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <SectionCard icon={<AiOutlineInfoCircle />} title="Захиалгын мэдээлэл">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    Буцаалт үүсгэх хүргэлтийн захиалгаа сонгоно уу.
                  </Text>
                }
                style={{ margin: 0, padding: '24px 0' }}
              >
                <Button type="primary" onClick={() => setVisibleOrder(true)}>
                  Захиалга сонгох
                </Button>
              </Empty>
            </SectionCard>

            <StepFooter
              htmlType="button"
              disabled
              extra={<Text type="secondary">Эхлээд захиалгаа сонгоно уу.</Text>}
            />
          </Space>
        </Form>

        {visibleOrder && (
          <ModalSelectOrder visible={visibleOrder} onCancel={() => setVisibleOrder(false)} />
        )}
      </>
    );
  }

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        /**
         * Step1 нь алхам солиход unmount болж формын instance нь шинээр
         * үүсдэг. Тиймээс сонгосон агуулах/салбар, тоо ширхгийг контекстэд
         * хадгалсан утгуудаас сэргээнэ — эс бөгөөс 2-р алхмаас буцаж ирэхэд
         * солих барааны блокууд хоосон харагдана.
         */
        initialValues={{ ...selectedOrder, ...(formDataStep1?.formValues ?? {}) }}
        // Бөглөж байх зуурын утгуудыг хадгална — refresh хийхэд ч сэргэнэ
        onValuesChange={(_, allValues) =>
          setFormDataStep1({ ...(formDataStep1 ?? {}), formValues: allValues })
        }
        onFinish={onFinish}
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <SectionCard
            icon={<AiOutlineInfoCircle />}
            title="Захиалгын мэдээлэл"
            action={<Button onClick={() => selectOrder(undefined)}>Захиалга солих</Button>}
          >
            <Descriptions
              {...descriptionProps}
              items={[
                { key: 'code', label: 'Хүргэлтийн захиалга', children: val(selectedOrder?.code) },
                { key: 'name', label: 'Нэр', children: val(selectedOrder?.customerName) },
                { key: 'phone', label: 'Утас', children: val(selectedOrder?.customerPhone) },
                { key: 'email', label: 'Мэйл', children: val(selectedOrder?.customerEmail) },
              ]}
            />
          </SectionCard>

          <SectionCard icon={<RollbackOutlined />} title="Буцаах бараа">
            <Product productType={PRODUCT_NOT_CHANGE} />
          </SectionCard>

          <SectionCard icon={<SwapOutlined />} title="Солих бараа" action={exchangeSwitch()}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              {fromChoice && (
                <Row gutter={16} style={{ maxWidth: FORM_MAX_WIDTH }}>
                  <Col xs={24} sm={12} xl={8}>
                    <Form.Item
                      label="Солих барааг татах"
                      name="exchangeChoice"
                      rules={[{ required: true, message: 'Солих барааг хаанаас татахаа сонгоно уу!' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        placeholder="Сонгох"
                        onChange={onChangeChoices}
                        options={[
                          { value: 'warehouse', label: 'Агуулах' },
                          { value: 'branch', label: 'Салбар' },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              {fromWarehouse &&
                warehouseList.map((warehouse) => (
                  <Warehouse
                    key={warehouse.id}
                    id={warehouse.id}
                    products={warehouse.products}
                    form={form}
                  />
                ))}

              {fromBranch &&
                branchList?.map((item) => (
                  <Branch key={item.id} id={item.id} products={item.products} form={form} />
                ))}

              {!fromWarehouse && !fromBranch && (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      Бараа солих шаардлагагүй бол энэ хэсгийг алгасаж болно.
                    </Text>
                  }
                  style={{ margin: 0, padding: '24px 0' }}
                />
              )}
            </Space>
          </SectionCard>

          <StepFooter />
        </Space>
      </Form>

      {visibleOrder && (
        <ModalSelectOrder visible={visibleOrder} onCancel={() => setVisibleOrder(false)} />
      )}
    </>
  );
};
